import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Form, Button, Spinner, Alert, Table, Row, Col, Modal, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import axios from 'axios';

const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(value);
};

const META_VENDAS = 15000.00; // Meta para comissão padrão
const META_INDICACAO = 10000.00; // Nova meta para comissão de indicação

const ComissaoRelatorio = ({ currentEntidade, currentUserRole, allFuncionarios }) => {
    const [comissoes, setComissoes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Estados para o modal de detalhes
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [commissionDetails, setCommissionDetails] = useState([]);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [selectedFuncionario, setSelectedFuncionario] = useState(null); // { id, nome }
    const [error, setError] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [commissionToConfirm, setCommissionToConfirm] = useState(null);

    // Estados para o modal de alteração de responsável
    const [showChangeOwnerModal, setShowChangeOwnerModal] = useState(false);
    const [commissionToChange, setCommissionToChange] = useState(null); // { id, nome, funcionario_id }
    const [newOwnerId, setNewOwnerId] = useState('');
    const [isSavingOwner, setIsSavingOwner] = useState(false);

    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    const [startDate, setStartDate] = useState(firstDayOfMonth);
    const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);

    const fetchComissoes = useCallback(async () => {
        if (!currentEntidade?.id) return;

        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get('/api/relatorios/comissoes-a-pagar', {
                params: {
                    entidade_id: currentEntidade.id,
                    data_inicio: startDate,
                    data_fim: endDate,
                }
            });
            setComissoes(response.data.comissoes || []);
        } catch (err) {
            setError('Não foi possível carregar o relatório de comissões.');
            console.error("Erro ao buscar comissões:", err);
        } finally {
            setIsLoading(false);
        }
    }, [currentEntidade, startDate, endDate]);

    const handleSearch = () => {
        fetchComissoes();
    };

    const handleViewDetails = async (funcionarioId, funcionarioNome) => {
        setSelectedFuncionario({ id: funcionarioId, nome: funcionarioNome });
        setShowDetailsModal(true);
        setIsLoadingDetails(true);
        setError(null); // Limpa erros anteriores

        try {
            const response = await axios.get('/api/relatorios/comissoes-detalhes', {
                params: {
                    entidade_id: currentEntidade.id,
                    data_inicio: startDate,
                    data_fim: endDate,
                    funcionario_id: funcionarioId,
                }
            });
            setCommissionDetails(response.data.detalhes || []);
        } catch (err) {
            setError('Não foi possível carregar os detalhes da comissão.');
            console.error("Erro ao buscar detalhes da comissão:", err);
            setCommissionDetails([]);
        } finally {
            setIsLoadingDetails(false);
        }
    };

    const handleCloseDetailsModal = () => {
        setShowDetailsModal(false);
        setCommissionDetails([]);
        setSelectedFuncionario(null);
    };

    const openConfirmModal = (pagamentoServicoId) => {
        setCommissionToConfirm(pagamentoServicoId);
        setShowConfirmModal(true);
    };

    const closeConfirmModal = () => {
        setCommissionToConfirm(null);
        setShowConfirmModal(false);
    };

    const executeConfirmCommission = async () => {
        if (!commissionToConfirm) return;
        try {
            await axios.post(`/api/relatorios/comissoes/confirmar/${commissionToConfirm}`);
            // Atualiza a lista de detalhes para refletir a mudança de status
            setCommissionDetails(prevDetails =>
                prevDetails.map(detalhe =>
                    detalhe.pagamento_servico_id === commissionToConfirm
                        ? { ...detalhe, status_comissao: 'Confirmada' }
                        : detalhe
                )
            );
            // Re-busca os dados da lista principal para atualizar os totais
            fetchComissoes();
            closeConfirmModal();
        } catch (err) {
            console.error("Erro ao confirmar comissão:", err);
            alert('Não foi possível confirmar a comissão. Tente novamente.'); // Manter o alerta para erros de API
        }
    };

    // Funções para o modal de alteração de responsável
    const openChangeOwnerModal = (detalhe) => {
        setCommissionToChange(detalhe);
        setNewOwnerId(detalhe.funcionario_id); // Pré-seleciona o dono atual
        setShowChangeOwnerModal(true);
    };

    const closeChangeOwnerModal = () => {
        setCommissionToChange(null);
        setShowChangeOwnerModal(false);
        setNewOwnerId('');
    };

    const executeChangeOwner = async () => {
        if (!commissionToChange || !newOwnerId) return;

        setIsSavingOwner(true);
        try {
            await axios.post(`/api/relatorios/comissoes/alterar-responsavel/${commissionToChange.pagamento_servico_id}`, {
                novo_funcionario_id: newOwnerId,
            });

            // Atualiza a UI localmente para refletir a mudança
            const novoFuncionario = allFuncionarios.find(f => f.id === parseInt(newOwnerId));
            setCommissionDetails(prevDetails =>
                prevDetails.map(d =>
                    d.pagamento_servico_id === commissionToChange.pagamento_servico_id
                        ? { ...d, funcionario_id: newOwnerId, funcionario_nome: novoFuncionario?.nome || 'Desconhecido' }
                        : d
                )
            );
            fetchComissoes(); // Re-busca a lista principal para atualizar os agrupamentos
            closeChangeOwnerModal();
        } catch (err) {
            alert(`Erro ao alterar responsável: ${err.response?.data?.message || 'Tente novamente.'}`);
        } finally {
            setIsSavingOwner(false);
        }
    };

    // Total vendido CONFIRMADO no período (soma apenas dos itens confirmados)
    const totalVendidoConfirmadoNoPeriodo = useMemo(() => {
        if (!commissionDetails || commissionDetails.length === 0) return 0;
        return commissionDetails.filter(d => ['Confirmada', 'Paga'].includes(d.status_comissao)).reduce((acc, item) => acc + parseFloat(item.valor_vendido), 0);
    }, [commissionDetails]);

    // Determina qual meta se aplica ao funcionário selecionado no modal.
    const metaAplicavel = useMemo(() => {
        if (!selectedFuncionario) return META_VENDAS; // Padrão para meta de vendas
        const funcionario = allFuncionarios.find(f => f.id === selectedFuncionario.id);
        return funcionario?.comissionavel ? META_VENDAS : META_INDICACAO;
    }, [selectedFuncionario, allFuncionarios]);

    // Verifica se a meta aplicável foi atingida para o funcionário no modal.
    const metaAtingida = useMemo(() => {
        return totalVendidoConfirmadoNoPeriodo >= metaAplicavel;
    }, [totalVendidoConfirmadoNoPeriodo, metaAplicavel]);

    // Recalcula o total de comissão a ser pago no período (apenas se a meta foi atingida e comissões confirmadas)
    const totalComissaoAPagarNoPeriodo = useMemo(() => {
        if (!metaAtingida) return 0;

        // A comissão total do período inclui tanto as confirmadas quanto as já pagas.
        return commissionDetails
            .filter(item => ['Confirmada', 'Paga'].includes(item.status_comissao))
            .reduce((acc, item) => acc + parseFloat(item.valor_comissao), 0);
    }, [commissionDetails, totalVendidoConfirmadoNoPeriodo, selectedFuncionario, allFuncionarios]);

    const totalGeralAPagar = useMemo(() => {
        return comissoes
            .filter(item => {
                const funcionario = allFuncionarios.find(f => f.id === item.funcionario_id);
                const temComissao = funcionario?.comissionavel;
                const metaAplicavel = temComissao ? META_VENDAS : META_INDICACAO;
                return parseFloat(item.total_vendido_confirmado) >= metaAplicavel;
            })
            .reduce((acc, item) => acc + parseFloat(item.total_comissao_confirmada), 0);
    }, [comissoes, allFuncionarios]);

    return (
        <Card className="h-100 shadow-sm">
            <Card.Header>
                <Card.Title as="h5" className="mb-0">
                    <span role="img" aria-label="comissão" className="me-2">💸</span>
                    Comissões a Pagar
                </Card.Title>
            </Card.Header>
            <Card.Body>
                <Form>
                    <Row className="align-items-end mb-3">
                        <Col md={4}>
                            <Form.Group controlId="comissaoStartDate">
                                <Form.Label size="sm">Data Início</Form.Label>
                                <Form.Control type="date" size="sm" value={startDate} onChange={e => setStartDate(e.target.value)} />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group controlId="comissaoEndDate">
                                <Form.Label size="sm">Data Fim</Form.Label>
                                <Form.Control type="date" size="sm" value={endDate} onChange={e => setEndDate(e.target.value)} />
                            </Form.Group>
                        </Col>
                        <Col md={4} className="d-grid">
                            <Button variant="primary" onClick={handleSearch} disabled={isLoading}>
                                {isLoading ? <Spinner as="span" size="sm" /> : 'Buscar'}
                            </Button>
                        </Col>
                    </Row>
                </Form>

                {error && <Alert variant="danger">{error}</Alert>}

                {!isLoading && !error && (
                    comissoes.length === 0 ? (
                        <Alert variant="info">Nenhuma comissão encontrada para o período.</Alert>
                    ) : (
                        <>
                            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                <Table striped hover responsive size="sm">
                                    <thead>
                                        <tr>
                                            {currentUserRole === 'Representante Legal' && <th>Profissional</th>}
                                            <th className="text-center">Serviços Comissionados</th>
                                            <th className="text-end">Total Vendido</th>
                                            <th className="text-end">Comissão a Pagar</th>
                                            <th>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {comissoes.map((item) => {
                                            const funcionario = allFuncionarios.find(f => f.id === item.funcionario_id);
                                            const temComissao = funcionario?.comissionavel;

                                            const temComissaoOriginaria = funcionario?.comissionavel_origem;

                                            // Determina qual meta aplicar com base no perfil do funcionário
                                            
                                            if (temComissao){
                                                const metaAplicavel = META_VENDAS;
                                            }
                                            else{
                                                const metaAplicavel = META_INDICACAO;
                                            }
                                            
                                            // metaAplicavel = temComissao ? META_VENDAS:META_INDICACAO;
                                            const metaAtingidaParaItem = parseFloat(item.total_vendido_confirmado) >= metaAplicavel;

                                            return (
                                            <tr key={item.funcionario_id}>
                                                {currentUserRole === 'Representante Legal' && <td>{item.funcionario_nome}</td>}
                                                <td className="text-center">{item.total_servicos_comissionados}</td>
                                                <td className="text-end" title={`Confirmado: ${formatCurrency(item.total_vendido_confirmado)}`}>{formatCurrency(item.total_vendido_confirmado)}</td>
                                                
                                                <td className={`text-end fw-bold ${!metaAtingidaParaItem ? 'text-danger' : 'text-success'}`}
                                                    // title={temComissao ? `Meta de Indicação: ${formatCurrency(metaAplicavel)}` : `Meta de Vendas: ${formatCurrency(metaAplicavel)}`}
                                                    title={temComissao ? `Meta de Vendas: ${formatCurrency(metaAplicavel)}` : `Meta de Indicação: ${formatCurrency(metaAplicavel)}`}

                                                >
                                                    {metaAtingidaParaItem ? (
                                                        formatCurrency(item.total_comissao_confirmada)
                                                    ) : (                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip>A comissão só é liberada após atingir a meta de {formatCurrency(metaAplicavel)} em serviços confirmados.</Tooltip>}
                                                        >
                                                            <span>{formatCurrency(0)} ℹ️</span>
                                                        </OverlayTrigger>
                                                    )}
                                                </td>
                                                <td>
                                                    <Button variant="outline-primary" size="sm" onClick={() => handleViewDetails(item.funcionario_id, item.funcionario_nome)}>
                                                        Detalhes
                                                    </Button>
                                                </td>
                                            </tr>
                                            )
                                            })
                                        }
                                    </tbody>
                                </Table>
                            </div>
                            {currentUserRole === 'Representante Legal' && comissoes.length > 1 && (
                                <div className="text-end fw-bold mt-3 border-top pt-2">
                                    Total Geral a Pagar: {formatCurrency(totalGeralAPagar)}
                                </div>
                            )}
                        </>
                    )
                )}
            </Card.Body>

            {/* Modal de Detalhes da Comissão */}
            <Modal show={showDetailsModal} onHide={handleCloseDetailsModal} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        Detalhes da Comissão - {selectedFuncionario?.nome}
                        <small className="d-block text-muted">
                            Período: {new Date(startDate + 'T00:00:00').toLocaleDateString('pt-BR')} a {new Date(endDate + 'T00:00:00').toLocaleDateString('pt-BR')}
                        </small>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isLoadingDetails ? (
                        <div className="text-center"><Spinner animation="border" /></div>
                    ) : error ? (
                        <Alert variant="danger">{error}</Alert>
                    ) : (
                        <>
                            <div className="d-flex justify-content-between align-items-center mb-3 p-2 rounded bg-light">
                                <div className='text-center'>
                                    <small className="d-block text-muted">Total Vendido no Período</small>
                                    <h5 className="mb-0 fw-bold">{formatCurrency(totalVendidoConfirmadoNoPeriodo)}</h5>
                                </div>
                                <div className='text-center'>
                                    <small className="d-block text-muted">Meta de Vendas</small>
                                    <h5 className="mb-0">{formatCurrency(allFuncionarios.find(f => f.id === selectedFuncionario?.id)?.comissionavel ? META_VENDAS : META_INDICACAO)}</h5>
                                </div>
                                <div className='text-center'>
                                    <small className="d-block text-muted">Status da Meta</small>
                                    {totalComissaoAPagarNoPeriodo > 0 ? <Badge bg="success">Atingida</Badge> : <Badge bg="warning">Pendente</Badge>}
                                </div>
                            </div>

                            {!metaAtingida && (
                                <Alert variant="info" className="text-center">
                                    <Alert.Heading>Aguardando cumprimento da meta</Alert.Heading>
                                    <p className="mb-0">Faltam <strong>{formatCurrency(metaAplicavel - totalVendidoConfirmadoNoPeriodo)}</strong> em vendas confirmadas para que as comissões sejam liberadas.</p>
                                </Alert>
                            )}

                            <Table striped bordered hover responsive size="sm">
                                <thead>
                                    <tr>
                                        <th>Data Pag.</th>
                                        <th>Paciente</th>
                                        <th>Serviço</th>
                                        <th className="text-end">Valor Comissão</th>
                                        <th className="text-center">Status</th>
                                        {currentUserRole === 'Representante Legal' && <th>Ações</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {commissionDetails.map((detalhe) => {
                                        const podeAlterar = currentUserRole === 'Representante Legal' && detalhe.status_comissao === 'Pendente';
                                        return (
                                        <tr key={detalhe.pagamento_servico_id}>
                                            <td>{new Date(detalhe.data_pagamento).toLocaleDateString('pt-BR')}</td>
                                            <td>{detalhe.paciente_nome}</td>
                                            <td>{detalhe.servico_nome}</td>
                                            <td className="text-end fw-bold">{formatCurrency(detalhe.valor_comissao)}</td>
                                            <td className="text-center">
                                                <Badge bg={detalhe.status_comissao === 'Paga' ? 'success' : detalhe.status_comissao === 'Confirmada' ? 'info' : 'warning'}>
                                                    {detalhe.status_comissao}
                                                </Badge>
                                            </td>
                                            {currentUserRole === 'Representante Legal' && (
                                                <td className="text-center">
                                                    {podeAlterar && (
                                                        <>
                                                            <Button variant="success" size="sm" onClick={() => openConfirmModal(detalhe.pagamento_servico_id)} className="me-1">Confirmar</Button>
                                                            <Button variant="outline-danger" size="sm" onClick={() => openChangeOwnerModal(detalhe)}>Alterar</Button>
                                                        </>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                        )
                                    })}
                                </tbody>
                            </Table>

                            <div className="text-end fw-bold mt-3 border-top pt-2">
                                Total Comissão a Pagar no Período: {formatCurrency(totalComissaoAPagarNoPeriodo)}
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseDetailsModal}>
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Confirmação */}
            <Modal show={showConfirmModal} onHide={closeConfirmModal} centered size="sm">
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar Comissão</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Tem certeza que deseja confirmar esta comissão? Esta ação não pode ser desfeita.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeConfirmModal}>
                        Cancelar
                    </Button>
                    <Button variant="success" onClick={executeConfirmCommission}>
                        Confirmar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Alteração de Responsável */}
            <Modal show={showChangeOwnerModal} onHide={closeChangeOwnerModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Alterar Responsável</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Selecione o novo profissional responsável pela comissão do serviço <strong>{commissionToChange?.servico_nome}</strong> para o paciente <strong>{commissionToChange?.paciente_nome}</strong>.</p>
                    <Form.Group controlId="novoResponsavel">
                        <Form.Label>Novo Responsável</Form.Label>
                        <Form.Select
                            value={newOwnerId}
                            onChange={(e) => setNewOwnerId(e.target.value)}
                        >
                            <option value="">Selecione um profissional...</option>
                            {/* Filtra para mostrar apenas funcionários comissionáveis */}
                            {(allFuncionarios || []).filter(f => f.comissionavel).map(func => (
                                <option key={func.id} value={func.id}>{func.nome}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeChangeOwnerModal} disabled={isSavingOwner}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={executeChangeOwner} disabled={isSavingOwner || !newOwnerId}>
                        {isSavingOwner ? <Spinner as="span" size="sm" /> : 'Salvar Alteração'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Card>
    );
};

export default ComissaoRelatorio;
